import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { isTokenExpired } from './authStore';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

let adminRefreshTimeoutId = null;

const isAdminUser = (user) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  const adminEmails = (process.env.REACT_APP_ADMIN_EMAILS || '')
    .split(',')
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean);
  const email = (user.email || '').toLowerCase();
  return Boolean(email && adminEmails.includes(email));
};

const useAdminAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      error: null,
      _hasHydrated: false,

      setHasHydrated: (value) => set({ _hasHydrated: value }),

      isAuthenticated: () => {
        const token = get().token;
        return !!token && !isTokenExpired(token);
      },

      getAuthUser: () => {
        const { user, token } = get();
        if (!token || isTokenExpired(token)) return user;
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          return {
            ...(user || {}),
            id: user?.id || payload.id,
            email: user?.email || payload.email,
            username: user?.username || payload.username,
            role: payload.role || user?.role,
            level: payload.level ?? user?.level,
          };
        } catch {
          return user;
        }
      },

      isAdmin: () => isAdminUser(get().getAuthUser()),

      refreshToken: async () => {
        const token = get().token;
        if (!token) return;

        try {
          const response = await fetch(`${API_URL}/auth/refresh-token`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          const data = await response.json();

          if (!response.ok) {
            get().signout();
            return;
          }

          set({ token: data.accessToken });
          get().scheduleTokenRefresh();
        } catch (error) {
          console.error('Admin token refresh error:', error);
          get().signout();
        }
      },

      scheduleTokenRefresh: () => {
        if (adminRefreshTimeoutId) {
          clearTimeout(adminRefreshTimeoutId);
        }
        adminRefreshTimeoutId = setTimeout(() => {
          get().refreshToken();
        }, 1 * 60 * 60 * 1000 + 55 * 60 * 1000);
      },

      signin: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${API_URL}/auth/signin`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || data.errors?.[0]?.msg || 'Sign in failed');
          }

          if (!isAdminUser(data.user)) {
            throw new Error('Admin access required. Sign in with an admin account.');
          }

          set({
            user: data.user,
            token: data.accessToken,
            isLoading: false,
            error: null,
          });

          get().scheduleTokenRefresh();
          return { success: true, user: data.user };
        } catch (error) {
          set({ isLoading: false, error: error.message });
          return { success: false, error: error.message };
        }
      },

      setAuth: (user, token) => {
        if (!isAdminUser(user)) {
          set({ error: 'Admin access required. Sign in with an admin account.' });
          return { success: false, error: 'Admin access required. Sign in with an admin account.' };
        }
        set({ user, token, error: null });
        get().scheduleTokenRefresh();
        return { success: true, user };
      },

      signout: async () => {
        const currentToken = get().token;

        if (adminRefreshTimeoutId) {
          clearTimeout(adminRefreshTimeoutId);
          adminRefreshTimeoutId = null;
        }

        if (currentToken) {
          try {
            await fetch(`${API_URL}/auth/logout`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${currentToken}`,
              },
            });
          } catch (error) {
            console.error('Admin logout error:', error);
          }
        }

        // Clear admin session only — do not touch website member auth
        set({ user: null, token: null, error: null });
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'admin-auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
      }),
      onRehydrateStorage: () => (state) => {
        useAdminAuthStore.setState({ _hasHydrated: true });
        if (state?.setHasHydrated) state.setHasHydrated(true);
        if (state?.token && !isTokenExpired(state.token)) {
          state.scheduleTokenRefresh?.();
        }
      },
    }
  )
);

if (typeof window !== 'undefined') {
  setTimeout(() => {
    if (!useAdminAuthStore.getState()._hasHydrated) {
      useAdminAuthStore.setState({ _hasHydrated: true });
    }
  }, 150);
}

export { isAdminUser };
export default useAdminAuthStore;
